import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Youtube, Search, ExternalLink, AlertTriangle } from "lucide-react";

const STATUS_META = {
  pending:          { label: "Pending",          color: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30" },
  in_review:        { label: "In Review",        color: "bg-violet-500/10 text-violet-400 border-violet-500/30" },
  action_required:  { label: "Action Required",  color: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  linked:           { label: "Linked",           color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  created:          { label: "Created",          color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  rejected:         { label: "Rejected",         color: "bg-rose-500/10 text-rose-400 border-rose-500/30" },
  unlink_requested: { label: "Unlink Requested", color: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  unlinked:         { label: "Unlinked",         color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30" },
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

export default function AdminYoutubeChannels() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get("/admin/yt-channels", { params: { q, status, type_filter: typeF } }).then((r) => setRows(r.data));
    api.get("/admin/yt-channels/summary").then((r) => setSummary(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, status, typeF]);

  const cards = summary ? [
    { k: "Total", v: summary.total },
    { k: "Pending", v: summary.by_status.pending, tone: "zinc" },
    { k: "In Review", v: summary.by_status.in_review, tone: "violet" },
    { k: "Action Required", v: summary.by_status.action_required, tone: "amber" },
    { k: "Existing Requests", v: summary.by_type.existing, tone: "cyan" },
    { k: "New Requests", v: summary.by_type.new, tone: "violet" },
    { k: "Linked", v: summary.by_status.linked, tone: "emerald" },
    { k: "Created", v: summary.by_status.created, tone: "emerald" },
  ] : [];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Administrator</div>
      <h1 className="mt-1 text-3xl font-bold flex items-center gap-3"><Youtube className="w-7 h-7 text-rose-400" /> YouTube Channels</h1>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {cards.map(c => <StatCard key={c.k} {...c} />)}
      </div>

      <div className="mt-6 rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4 flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-yt-search"
            placeholder="Search by Request ID, Channel, User or Channel ID" className="pl-9 bg-zinc-900 border-zinc-800" />
        </div>
        <FilterSelect v={status} onV={setStatus} label="Status" testid="admin-yt-filter-status"
          opts={[{v:"all",l:"All"},{v:"pending",l:"Pending"},{v:"in_review",l:"In Review"},{v:"action_required",l:"Action Required"},{v:"linked",l:"Linked"},{v:"created",l:"Created"},{v:"rejected",l:"Rejected"},{v:"unlink_requested",l:"Unlink Requested"},{v:"unlinked",l:"Unlinked"}]} />
        <FilterSelect v={typeF} onV={setTypeF} label="Type" testid="admin-yt-filter-type"
          opts={[{v:"all",l:"All"},{v:"existing",l:"Existing Channel"},{v:"new",l:"New Channel"}]} />
      </div>

      <div className="mt-4 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-3">Request ID</th><th className="p-3">User</th><th className="p-3">Type</th>
              <th className="p-3">Channel Name</th><th className="p-3">Email</th>
              <th className="p-3">Channel URL</th><th className="p-3">Channel ID</th>
              <th className="p-3">Status</th><th className="p-3">Submitted</th><th className="p-3">Updated</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody data-testid="admin-yt-tbody">
            {rows.length === 0 && <tr><td colSpan="11" className="p-8 text-center text-zinc-500">No requests found</td></tr>}
            {rows.map(r => {
              const m = STATUS_META[r.status] || STATUS_META.pending;
              return (
                <tr key={r.id} data-testid={`admin-yt-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                  <td className="p-3 font-mono text-xs">{r.request_code}</td>
                  <td className="p-3">
                    <div>{r.user_full_name}</div>
                    <div className="text-[10px] text-zinc-500">{r.user_email}</div>
                  </td>
                  <td className="p-3 text-xs">{r.request_type === "existing" ? "Existing" : "New"}</td>
                  <td className="p-3">{r.channel_name || r.desired_channel_name || "—"}</td>
                  <td className="p-3 text-xs">{r.email || "—"}</td>
                  <td className="p-3 text-xs truncate max-w-[160px]" title={r.channel_url}>{r.channel_url || "—"}</td>
                  <td className="p-3 font-mono text-[11px]">{r.channel_id || "—"}</td>
                  <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded-md text-xs border ${m.color}`}>{m.label}</span></td>
                  <td className="p-3 text-xs text-zinc-500">{fmt(r.created_at)}</td>
                  <td className="p-3 text-xs text-zinc-500">{fmt(r.updated_at)}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setSelected(r)} data-testid={`admin-yt-open-${r.id}`} className="text-cyan-400 text-xs">Manage</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <RequestManage req={selected} onClose={() => { setSelected(null); load(); }} />
    </div>
  );
}

function StatCard({ k, v, tone }) {
  const c = tone === "emerald" ? "text-emerald-400" : tone === "amber" ? "text-amber-400" : tone === "violet" ? "text-violet-400" : tone === "cyan" ? "text-cyan-400" : "text-zinc-100";
  return <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-950/60"><div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div><div className={`mt-1.5 text-2xl font-semibold ${c}`}>{v}</div></div>;
}
function FilterSelect({ v, onV, label, opts, testid }) {
  return (<div><div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
    <Select value={v} onValueChange={onV}><SelectTrigger data-testid={testid} className="bg-zinc-900 border-zinc-800 h-9 min-w-[140px] text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>{opts.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent></Select>
  </div>);
}

function RequestManage({ req, onClose }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});

  useEffect(() => {
    if (!req) { setDetail(null); return; }
    api.get(`/admin/yt-channels/${req.id}`).then((r) => {
      setDetail(r.data);
      setF({
        status: r.data.status,
        channel_name: r.data.channel_name || "",
        channel_url: r.data.channel_url || "",
        channel_id: r.data.channel_id || "",
        admin_note_public: r.data.admin_note_public || "",
        admin_note_internal: r.data.admin_note_internal || "",
        action_required_message: r.data.action_required_message || "",
      });
    });
  }, [req]);

  const save = async (statusOverride) => {
    setBusy(true);
    try {
      const body = { ...f };
      if (statusOverride) body.status = statusOverride;
      await api.patch(`/admin/yt-channels/${req.id}`, body);
      toast.success("Updated");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl" data-testid="admin-yt-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">{req?.request_code}
            <span className={`inline-block px-2 py-0.5 rounded-md text-xs border ${STATUS_META[req?.status || "pending"]?.color || ""}`}>{STATUS_META[req?.status || "pending"]?.label}</span>
          </DialogTitle>
          <DialogDescription className="text-zinc-500">{req?.request_type === "existing" ? "Existing Channel" : "New Channel"} · {req?.user_email}</DialogDescription>
        </DialogHeader>
        {detail && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {detail.possible_duplicates?.length > 0 && (
              <div className="p-3 rounded-lg border border-amber-500/40 bg-amber-500/5 text-xs">
                <div className="flex items-center gap-2 text-amber-400 font-semibold"><AlertTriangle className="w-4 h-4" /> Possible Duplicate Channel</div>
                {detail.possible_duplicates.map(d => (
                  <div key={d.id} className="mt-1 text-zinc-300">
                    Already linked under <span className="font-mono">{d.request_code}</span> by {d.user_email} — {d.channel_name}
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <F label="Channel Name"><Input value={f.channel_name} onChange={(e) => setF({...f, channel_name: e.target.value})} data-testid="admin-yt-name" className="bg-zinc-900 border-zinc-800" /></F>
              <F label="Channel URL"><Input value={f.channel_url} onChange={(e) => setF({...f, channel_url: e.target.value})} data-testid="admin-yt-url" className="bg-zinc-900 border-zinc-800" /></F>
              <F label="Channel ID"><Input value={f.channel_id} onChange={(e) => setF({...f, channel_id: e.target.value})} data-testid="admin-yt-cid" className="bg-zinc-900 border-zinc-800 font-mono" /></F>
              <F label="Status">
                <Select value={f.status} onValueChange={(v) => setF({...f, status: v})}>
                  <SelectTrigger data-testid="admin-yt-status" className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
            </div>
            {f.status === "action_required" && (
              <F label="Message to user"><Textarea value={f.action_required_message} onChange={(e) => setF({...f, action_required_message: e.target.value})} data-testid="admin-yt-actionmsg" className="bg-zinc-900 border-zinc-800" /></F>
            )}
            <F label="Admin note (visible to user)"><Textarea value={f.admin_note_public} onChange={(e) => setF({...f, admin_note_public: e.target.value})} data-testid="admin-yt-note-public" className="bg-zinc-900 border-zinc-800" /></F>
            <F label="Admin note (INTERNAL — hidden from user)"><Textarea value={f.admin_note_internal} onChange={(e) => setF({...f, admin_note_internal: e.target.value})} data-testid="admin-yt-note-internal" className="bg-zinc-900 border-zinc-800" /></F>
            {detail.user_notes && (
              <div className="p-3 rounded-lg border border-zinc-800 bg-zinc-900 text-sm">
                <div className="text-zinc-500 text-xs uppercase tracking-wider mb-1">User notes</div>{detail.user_notes}
              </div>
            )}
            {detail.unlink_reason && (
              <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 text-sm">
                <div className="text-amber-400 text-xs uppercase tracking-wider mb-1">Unlink reason</div>{detail.unlink_reason}
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Activity</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(detail.history || []).map((h, i) => (
                  <div key={i} className="text-xs text-zinc-400 flex gap-2 flex-wrap">
                    <span className="font-mono text-zinc-500">{fmt(h.at)}</span>
                    <span className="text-zinc-300">{h.action}</span>
                    {h.old_status && h.new_status && h.old_status !== h.new_status && <span className="text-emerald-400">{h.old_status} → {h.new_status}</span>}
                    <span className="text-zinc-500">by {h.by}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} className="border-zinc-700 hover:bg-zinc-900">Close</Button>
          <Button onClick={() => save()} disabled={busy} data-testid="admin-yt-save" className="bg-cyan-500 text-zinc-950 hover:bg-cyan-600">Save</Button>
          {req?.request_type === "existing" && <Button onClick={() => save("linked")} disabled={busy} data-testid="admin-yt-mark-linked" className="bg-emerald-500 text-zinc-950 hover:bg-emerald-600">Mark Linked</Button>}
          {req?.request_type === "new" && <Button onClick={() => save("created")} disabled={busy} data-testid="admin-yt-mark-created" className="bg-emerald-500 text-zinc-950 hover:bg-emerald-600">Mark Created</Button>}
          {req?.status === "unlink_requested" && <Button onClick={() => save("unlinked")} disabled={busy} data-testid="admin-yt-mark-unlinked" className="bg-zinc-600 hover:bg-zinc-500">Approve Unlink</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function F({ label, children }) {
  return <div><Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
